/**
 * Small pieces shared by every incident surface (map popover, Incidents
 * tab, detail modal): kind icons, the agent status chip and the
 * Monitor-with-AI button.
 */
import { Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LandscapeIcon from '@mui/icons-material/Landscape';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { IncidentSummary } from '../../api/incidentLog';
import { AgentSessionSummary, useStartAgentSession } from '../../api/agentSessions';

export const TEAL = '#00d4ad';

export function kindIcon(kind: string, size?: number) {
    const sx = size ? { fontSize: size } : {};
    if (kind === 'lightning') return <BoltIcon sx={{ ...sx, color: '#a78bfa' }} />;
    if (kind === 'ignition') return <WhatshotIcon sx={{ ...sx, color: '#ff2d95' }} />;
    if (kind === 'burnt_area') return <LandscapeIcon sx={{ ...sx, color: '#b91c1c' }} />;
    return <LocalFireDepartmentIcon sx={{ ...sx, color: '#ff6b35' }} />;
}

export const AGENT_STATUS_LABEL: Record<AgentSessionSummary['status'], string> = {
    active: 'Agent monitoring',
    closed: 'Agent closed',
    stopped: 'Agent stopped',
    failed: 'Agent failed',
};

export function AgentStatusChip({ session }: { session: AgentSessionSummary }) {
    const active = session.status === 'active';
    return (
        <Chip
            size="small"
            icon={<SmartToyIcon sx={{ fontSize: 16 }} />}
            label={AGENT_STATUS_LABEL[session.status]}
            sx={{
                flexShrink: 0,
                color: active ? TEAL : 'rgba(255,255,255,0.6)',
                borderColor: active ? TEAL : 'rgba(255,255,255,0.3)',
                '& .MuiChip-icon': { color: active ? TEAL : 'rgba(255,255,255,0.6)' },
            }}
            variant="outlined"
        />
    );
}

export function MonitorButton({
    projectId,
    incident,
    onStarted,
    label,
}: {
    projectId: string | number;
    incident: IncidentSummary;
    onStarted: () => void;
    /** Render as a labelled chip-style button rather than an icon. */
    label?: string;
}) {
    const start = useStartAgentSession(projectId, incident.id);
    const button = label ? (
        <Chip
            size="small"
            clickable
            disabled={start.isPending}
            icon={
                start.isPending ? (
                    <CircularProgress size={14} sx={{ color: TEAL }} />
                ) : (
                    <SmartToyIcon sx={{ fontSize: 16 }} />
                )
            }
            label={label}
            onClick={(e) => {
                e.stopPropagation();
                start.mutate(undefined, { onSuccess: onStarted });
            }}
            variant="outlined"
            sx={{
                color: TEAL,
                borderColor: 'rgba(0, 212, 173, 0.5)',
                '& .MuiChip-icon': { color: TEAL },
                '&:hover': { backgroundColor: 'rgba(0, 212, 173, 0.08)' },
            }}
        />
    ) : (
        <IconButton
            size="small"
            disabled={start.isPending}
            onClick={(e) => {
                e.stopPropagation();
                start.mutate(undefined, { onSuccess: onStarted });
            }}
            sx={{
                color: TEAL,
                border: '1px solid rgba(0, 212, 173, 0.4)',
                '&:hover': {
                    borderColor: TEAL,
                    backgroundColor: 'rgba(0, 212, 173, 0.08)',
                },
            }}
        >
            {start.isPending ? (
                <CircularProgress size={18} sx={{ color: TEAL }} />
            ) : (
                <SmartToyIcon fontSize="small" />
            )}
        </IconButton>
    );
    return (
        <Tooltip title="Monitor with AI — an agent watches this incident, runs simulations, notifies subscribers and prepares operational briefs. Follow and message it from the incident details.">
            <span style={{ flexShrink: 0 }}>{button}</span>
        </Tooltip>
    );
}
